import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type ColumnSizingState,
  type SortingState,
  type OnChangeFn,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ExternalLink, X, Link2 } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import toast from 'react-hot-toast';
import {
  fetchCampaigns,
  updateCampaign,
  type Campaign,
  type FordShareEntry,
} from '@/api/campaigns';
import { FordShareModal } from '@/components/FordShareModal';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Hash, Plus, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { FlagIcon } from '@/components/FlagIcon';
import { PAGE_PADDING, tableScrollMaxHeight } from '@/components/layout/layoutConstants';
import { useHeaderHeight } from '@/components/layout/useHeaderHeight';
import { DcoBadge, isDcoEnabled } from '@/components/DcoBadge';
import { PlatformIcon, normalizePlatformLabel } from '@/components/PlatformIcon';
import { getPermissions } from '@/api/users';
import { useCurrentUser } from '@/hooks/useUserRole';
import { getDefaultMarketFilter } from '@/utils/defaultMarketFilter';
import { getDefaultChannelFilter } from '@/utils/defaultChannelFilter';
import { StatusProgressionModal } from '@/components/StatusProgressionModal';
import { ColumnSettings, type ColumnConfig } from '@/components/ColumnSettings';
import {
  loadColumnSettings,
  loadColumnSettingsAsync,
  saveColumnSettings,
  DEFAULT_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  type TableColumn,
} from '@/utils/columnConfig';
import { findCampaignByCampaignId } from '@/utils/campaignLookup';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { ContentTrackerHeader } from '@/pages/content-tracker/components/Header';
import { ContentTrackerTableSkeleton } from '@/pages/content-tracker/components/ContentTrackerTableSkeleton';
import { MapSyncFooter } from '@/components/MapSyncFooter';

// FordShare URL template
const FORDSHARE_ACTIVITY_URL_TEMPLATE =
  'https://ford.brandshare.net/global/advert/ActivityDetails.aspx?activityId=';

// Estimated height (px) of a single campaign table row, used by the row
// virtualizer. Rows are single-line (whitespace-nowrap/truncate) so height is
// uniform; this matches a real rendered row.
const ROW_HEIGHT = 49;

// Estimated height (px) of a plan-id group header row.
const GROUP_ROW_HEIGHT = 37;

// Stand-in collapse key for the "No Plan ID" group (plan_id === null).
const NO_PLAN_KEY = '__no_plan__';

// Flattened row model: the table renders one virtualized list where campaigns
// are grouped under their plan_id, with a header row opening each group.
type CtTableRow =
  | {
      kind: 'group';
      planId: string | null;
      count: number;
      hasDco: boolean;
      collapsed: boolean;
      approvedAt: string | null;
    }
  | { kind: 'campaign'; campaign: Campaign; ordinal: number };

// Status badge configuration
const STATUS_CONFIG: Record<string, { className: string; label: string }> = {
  waiting_for_approval: {
    className: 'bg-yellow-100 text-yellow-800',
    label: 'Waiting for Approval',
  },
  waiting_for_client_brief: {
    className: 'bg-gray-100 text-gray-800',
    label: 'Waiting for Client Brief',
  },
  asset_in_development: {
    className: 'bg-slate-100 text-slate-800',
    label: 'Asset in Development',
  },
  ad_copy_creation: {
    className: 'bg-blue-100 text-blue-800',
    label: 'Ad Copy Creation',
  },
  activation_in_setup: {
    className: 'bg-green-100 text-green-800',
    label: 'Activation in Setup',
  },
  waiting_for_preview_approval: {
    className: 'bg-orange-100 text-orange-800',
    label: 'Waiting for Preview Approval',
  },
  preview_approved: {
    className: 'bg-emerald-100 text-emerald-800',
    label: 'Preview Approved',
  },
  campaign_live: {
    className: 'bg-purple-100 text-purple-800',
    label: 'Campaign Live',
  },
};

const getStatusConfig = (status: string | null) => {
  if (!status) return null;
  return STATUS_CONFIG[status] || { className: 'bg-gray-100 text-gray-800', label: status };
};

// Status progression order (7 stages)
const STATUS_ORDER = [
  'waiting_for_client_brief',
  'asset_in_development',
  'ad_copy_creation',
  'activation_in_setup',
  'waiting_for_preview_approval',
  'preview_approved',
  'campaign_live',
];

const getStatusStep = (status: string | null): { current: number; total: number } | null => {
  if (!status) return null;
  const index = STATUS_ORDER.indexOf(status);
  if (index === -1) return null;
  return { current: index + 1, total: STATUS_ORDER.length };
};

// Status dot colors matching badge colors
const STATUS_DOT_COLORS: Record<string, string> = {
  waiting_for_client_brief: 'bg-gray-500',
  asset_in_development: 'bg-slate-500',
  ad_copy_creation: 'bg-blue-500',
  activation_in_setup: 'bg-green-500',
  waiting_for_preview_approval: 'bg-orange-500',
  preview_approved: 'bg-emerald-500',
  campaign_live: 'bg-purple-500',
};

// Progress dots component
const ProgressDots = ({
  current,
  total,
  status,
}: {
  current: number;
  total: number;
  status: string;
}) => {
  const activeColor = STATUS_DOT_COLORS[status] || 'bg-gray-500';
  return (
    <div className="flex items-center justify-center gap-1 mt-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`w-1.5 h-1.5 rounded-full transition-colors ${
            i < current ? activeColor : 'bg-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

// SLA row background colors based on SLA status
const SLA_ROW_COLORS: Record<string, string> = {
  on_track: 'bg-green-50', // Light green - plenty of time
  warning: 'bg-yellow-50', // Light yellow - approaching deadline
  breached: 'bg-red-50', // Light red - past deadline
  not_applicable: '', // No special background
};

const getSlaRowClass = (slaStatus: string | null): string => {
  if (!slaStatus) return '';
  return SLA_ROW_COLORS[slaStatus] || '';
};

// Map column keys to API field names for permission checks
const getApiFieldName = (columnKey: string): string => {
  const fieldMapping: Record<string, string> = {
    start_date: 'campaign_start_date',
    end_date: 'campaign_end_date',
  };
  return fieldMapping[columnKey] || columnKey;
};

interface EditableFieldProps {
  value: string | null;
  onChange: (value: string) => void;
  type?: 'text' | 'date' | 'url';
  displayValue?: string | null;
  disabled?: boolean;
  validateInteger?: boolean;
}

function EditableField({
  value,
  onChange,
  type = 'text',
  displayValue,
  disabled = false,
  validateInteger = false,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');

  // Sync localValue with value prop when it changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleSave = () => {
    // Validate integer if required
    if (validateInteger) {
      const trimmedValue = localValue.trim();
      // Only validate if the trimmed value is non-empty
      // Empty values are allowed (to clear the field)
      if (trimmedValue && !/^\d+$/.test(trimmedValue)) {
        toast.error('FordShare Activity ID must be a valid integer');
        return;
      }
      // Save the trimmed value for integer fields
      onChange(trimmedValue);
      setIsEditing(false);
      return;
    }
    onChange(localValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(value || '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type={type}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          className="text-sm border border-blue-400 rounded px-2 py-1 w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
        />
        <button
          onClick={handleSave}
          className="text-green-600 hover:text-green-800 text-xs px-1"
          title="Save"
          aria-label="Save"
        >
          ✓
        </button>
        <button
          onClick={handleCancel}
          className="text-red-600 hover:text-red-800 text-xs px-1"
          title="Cancel"
          aria-label="Cancel"
        >
          ✕
        </button>
      </div>
    );
  }

  // Use displayValue for display if provided, otherwise use value
  const textToShow = displayValue ?? value;

  if (disabled) {
    return <div className="min-h-[2rem] flex items-center">{textToShow || '-'}</div>;
  }

  return (
    <div
      onClick={() => setIsEditing(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
      role="button"
      tabIndex={0}
      className="cursor-pointer hover:bg-blue-50 rounded px-2 py-1 min-h-[2rem] flex items-center"
      title="Click to edit"
    >
      {textToShow || <span className="text-gray-400 italic">Click to add</span>}
    </div>
  );
}

interface EditableBooleanProps {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
}

function EditableBoolean({ value, onChange }: EditableBooleanProps) {
  return (
    <select
      value={value === null ? '' : value ? 'true' : 'false'}
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(null);
        } else {
          onChange(e.target.value === 'true');
        }
      }}
      className="text-sm border border-blue-400 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">-</option>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </select>
  );
}

// Preview link cell with chip display and popover editor
function PreviewLinkCell({
  links,
  canEdit,
  onUpdate,
}: {
  links: string[];
  canEdit: boolean;
  onUpdate: (links: string[]) => void;
}) {
  const [newLink, setNewLink] = useState('');
  const [open, setOpen] = useState(false);

  const getHostname = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  const isHttpUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleAdd = () => {
    const trimmed = newLink.trim();
    if (!trimmed) return;
    if (!isHttpUrl(trimmed)) {
      return;
    }
    const updated = [...links, trimmed];
    onUpdate(updated);
    setNewLink('');
  };

  const handleRemove = (index: number) => {
    const updated = links.filter((_, i) => i !== index);
    onUpdate(updated);
  };

  const chipClass =
    'px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg inline-flex items-center gap-1.5 hover:bg-slate-200 transition-colors max-w-[200px]';

  const addBtnClass =
    'w-7 h-7 rounded-lg inline-flex items-center justify-center border-2 border-dashed border-slate-400 text-slate-400 hover:border-slate-600 hover:text-slate-600 transition-all opacity-0 group-hover/plcell:opacity-100';

  // Read-only chips — only render as links for http(s) URLs
  const chips = links.map((link, i) => {
    const safe = isHttpUrl(link);
    const hostname = getHostname(link);
    if (safe) {
      return (
        <a
          key={i}
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={chipClass}
          onClick={(e) => e.stopPropagation()}
          title={link}
        >
          <Link2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="font-medium truncate">{hostname}</span>
        </a>
      );
    }
    return (
      <span key={i} className={chipClass} title={link}>
        <Link2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="font-medium truncate">{hostname}</span>
      </span>
    );
  });

  if (!canEdit) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {links.length > 0 ? chips : <span className="text-slate-400">-</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 group/plcell">
      <Popover open={open} onOpenChange={setOpen}>
        {chips}
        <PopoverTrigger asChild>
          <button className={addBtnClass}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" side="bottom" align="start">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Preview Links
              </p>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {links.length > 0 && (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {links.map((link, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-sm group/plitem">
                    <span className="truncate flex-1 text-slate-700" title={link}>
                      {getHostname(link)}
                    </span>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 rounded text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button
                      onClick={() => handleRemove(i)}
                      className="p-1 rounded text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-1.5">
              <input
                type="url"
                placeholder="https://..."
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                className="flex-1 px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAdd}
                disabled={!newLink.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Truncated cell content with hover expand.
// With `table-layout: fixed` and per-column widths driven by the <colgroup>,
// the cell fills its column and a plain `truncate` reveals more text as the
// column is widened. The hover tooltip remains a fallback for narrow columns.
function TruncatedCell({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <div className="relative group" aria-label={title}>
      {/* Truncated version - always visible, fills the (fixed-width) cell */}
      <div className="truncate" aria-hidden={title ? true : undefined}>
        {children}
      </div>
      {/* Expanded version - shows on hover */}
      {title && (
        <div
          role="tooltip"
          className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-white border border-gray-300 shadow-lg rounded px-2 py-1 text-sm whitespace-pre-line min-w-max max-w-xs"
        >
          {title}
        </div>
      )}
    </div>
  );
}

function CampaignRow({
  campaign,
  rowIndex,
  stripeIndex,
  measureRef,
  visibleColumns,
  editableFields,
  isHighlighted,
  showColors,
  openStatusModal,
  onStatusModalClose,
  onHighlight,
}: {
  campaign: Campaign;
  /** Index in the virtualized row model — used for measurement (data-index) */
  rowIndex: number;
  /** Campaign-only ordinal — used for zebra striping so group headers don't break the pattern */
  stripeIndex?: number;
  measureRef?: (el: HTMLTableRowElement | null) => void;
  visibleColumns: TableColumn[];
  editableFields: string[];
  isHighlighted?: boolean;
  showColors?: boolean;
  openStatusModal?: boolean;
  onStatusModalClose?: () => void;
  onHighlight?: (campaignId: string) => void;
}) {
  const queryClient = useQueryClient();
  const pendingHighlight = useRef(false);

  const mutation = useMutation({
    mutationFn: (data: Parameters<typeof updateCampaign>[1]) => updateCampaign(campaign.id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      // When a campaign goes live, backend locks search asset versions —
      // invalidate the search-assets cache so the UI reflects the lock immediately.
      if ('live' in variables && variables.live === true) {
        queryClient.invalidateQueries({ queryKey: ['search-assets', campaign.id] });
      }
      if (pendingHighlight.current) {
        pendingHighlight.current = false;
        onHighlight?.(campaign.id);
      }
    },
    onError: (error: unknown) => {
      // Show error message to user
      let errorMessage = 'Failed to update campaign';
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response;
        errorMessage = response?.data?.detail || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    },
  });

  // State for modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showFordShareModal, setShowFordShareModal] = useState(false);

  const handleFordShareSave = (entries: FordShareEntry[]) => {
    const newEntries = entries.length > 0 ? entries : [];

    // Optimistic update — show immediately, roll back on failure
    const previousData = queryClient.getQueryData<{ campaigns: Campaign[] }>(['campaigns']);
    queryClient.setQueryData<{ campaigns: Campaign[] }>(['campaigns'], (old) => {
      if (!old) return old;
      return {
        ...old,
        campaigns: old.campaigns.map((c) =>
          c.id === campaign.id ? { ...c, fordshare_entries: newEntries } : c,
        ),
      };
    });

    mutation.mutate(
      { fordshare_entries: newEntries },
      {
        onError: () => {
          queryClient.setQueryData(['campaigns'], previousData);
        },
      },
    );
  };

  // Open status modal if requested from parent (e.g., deep link)
  useEffect(() => {
    if (openStatusModal && !showStatusModal) {
      setShowStatusModal(true);
    }
  }, [openStatusModal, showStatusModal]);

  // Handle modal close, notifying parent if needed
  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    onStatusModalClose?.();
  };

  const handleFieldUpdate = (field: string, value: string | string[] | boolean | null) => {
    const apiField = getApiFieldName(field);
    mutation.mutate({ [apiField]: value });
  };

  const isUpdating = mutation.isPending;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      // Extract date portion (YYYY-MM-DD) from ISO string or date-only string
      const isoDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const parts = isoDate.split('-');

      // Parse as string to avoid timezone issues with Date object
      if (parts.length === 3) {
        const [year, month, day] = parts;
        // Basic validation
        if (
          year &&
          month &&
          day &&
          !isNaN(Number(year)) &&
          !isNaN(Number(month)) &&
          !isNaN(Number(day))
        ) {
          return `${day}/${month}/${year}`;
        }
      }

      // If parsing failed, return original string
      return dateStr;
    } catch {
      // On error, return original string
      return dateStr;
    }
  };

  const renderCell = (column: TableColumn) => {
    const cellClass = `px-6 py-4 text-sm align-middle${
      column.id === 'last_edited'
        ? ' text-gray-500 sticky right-0 bg-slate-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]'
        : ''
    }`;

    // Type guard to check if column key is a valid Campaign property
    const isCampaignKey = (key: string): key is keyof Campaign => {
      return (
        key !== 'actions' && key !== 'status' && key !== 'last_edited' && key !== 'campaign_id'
      );
    };

    switch (column.id) {
      case 'actions':
        return (
          <td key={column.id} className={`${cellClass} text-center`}>
            <div className="inline-flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/campaigns/$campaignId/preview"
                    params={{ campaignId: campaign.id }}
                    onClick={(e) => {
                      e.stopPropagation();
                      sessionStorage.setItem('ct-last-viewed-campaign', campaign.id);
                    }}
                    className="inline-flex items-center justify-center w-8 h-8 text-slate-400 rounded hover:text-[#0014cc] hover:scale-110 active:scale-95 transition-all"
                    aria-label="View ad preview"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="left">View ad preview and details</TooltipContent>
              </Tooltip>
            </div>
          </td>
        );

      case 'status': {
        const statusConfig = getStatusConfig(campaign.status);
        const statusStep = getStatusStep(campaign.status);
        return (
          <td key={column.id} className={`${cellClass} text-center`}>
            {statusConfig ? (
              <div className="inline-flex flex-col items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStatusModal(true);
                  }}
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.className} hover:ring-2 hover:ring-offset-1 hover:ring-gray-300 transition-all cursor-pointer`}
                  title="Click to view status progression"
                  aria-label={`View status progression for campaign: ${statusConfig.label}`}
                >
                  {statusConfig.label}
                  <svg
                    className="w-3 h-3 opacity-50"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
                {statusStep && campaign.status && (
                  <ProgressDots
                    current={statusStep.current}
                    total={statusStep.total}
                    status={campaign.status}
                  />
                )}
              </div>
            ) : (
              '-'
            )}
          </td>
        );
      }

      case 'campaign_id':
        return (
          <td key={column.id} className={cellClass}>
            {/* DCO marker lives on the locked Campaign ID column so DCO rows
                stay recognisable even when the DCO column is hidden/reordered */}
            <div className="flex items-center gap-1.5">
              {isDcoEnabled(campaign.dco_status) && <DcoBadge iconOnly />}
              <div className="flex-1 min-w-0">
                <TruncatedCell title={campaign.campaign_id}>{campaign.campaign_id}</TruncatedCell>
              </div>
            </div>
          </td>
        );

      case 'last_edited':
        return (
          <td key={column.id} className={cellClass}>
            <TruncatedCell
              title={
                campaign.last_edited_by
                  ? [campaign.last_edited_by.name, formatDate(campaign.last_edited_at)]
                      .filter(Boolean)
                      .join('\n')
                  : undefined
              }
            >
              {isUpdating ? (
                <div className="flex items-center gap-2 text-blue-600">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="text-xs font-medium">Saving...</span>
                </div>
              ) : campaign.last_edited_by ? (
                <div>
                  <div className="truncate">{campaign.last_edited_by.name}</div>
                  <div className="text-xs truncate">{formatDate(campaign.last_edited_at)}</div>
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </TruncatedCell>
          </td>
        );

      case 'market':
        return (
          <td key={column.id} className={cellClass}>
            {campaign.market ? (
              <span className="px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg inline-flex items-center gap-1.5">
                <FlagIcon countryCode={campaign.market} className="w-4 h-auto rounded-sm" />
                <span>{campaign.market}</span>
              </span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </td>
        );

      case 'platform':
        return (
          <td key={column.id} className={cellClass}>
            {campaign.platform ? (
              <PlatformIcon platform={campaign.platform} />
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </td>
        );

      default: {
        // Handle editable and non-editable fields
        // Safety check: ensure column.key is a valid Campaign property
        if (!isCampaignKey(column.key)) {
          // This should never happen due to switch statement, but handle gracefully
          return (
            <td key={column.id} className={cellClass}>
              -
            </td>
          );
        }

        // FordShare entries — badge style matching market/platform badges
        if (column.key === 'fordshare_entries') {
          const entries: FordShareEntry[] =
            (campaign.fordshare_entries as FordShareEntry[] | null) ?? [];
          const canEdit = editableFields.includes('fordshare_entries');
          return (
            <td key={column.id} className={`${cellClass} group/fscell`}>
              <div className="flex flex-col items-start gap-1.5">
                {entries.map((entry, i) => {
                  const isLast = i === entries.length - 1;
                  const badge = (
                    <HoverCard key={entry.id}>
                      <HoverCardTrigger asChild>
                        <button
                          onClick={() => canEdit && setShowFordShareModal(true)}
                          className={`px-2.5 py-1 text-sm bg-slate-100 text-slate-700 rounded-lg inline-flex items-center gap-1.5 hover:bg-slate-200 transition-colors ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                        >
                          <Hash className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-medium">{entry.id}</span>
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent
                        className="w-auto min-w-[200px]"
                        side="bottom"
                        align="start"
                      >
                        <div className="space-y-2.5">
                          <div className="space-y-0.5">
                            <p className="text-sm text-slate-800 flex items-center gap-1.5 group/hcid">
                              <span className="text-slate-400 font-normal">ID:</span>
                              <span className="font-semibold">{entry.id}</span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(entry.id);
                                  toast.success('FordShare ID copied');
                                }}
                                className="p-0.5 rounded text-slate-300 hover:text-slate-500 transition-colors opacity-0 group-hover/hcid:opacity-100"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </p>
                            {entry.delivery_date && (
                              <p className="text-xs text-slate-400">
                                Delivery date:{' '}
                                {new Date(entry.delivery_date + 'T00:00:00').toLocaleDateString(
                                  'en-GB',
                                  { day: 'numeric', month: 'short', year: 'numeric' },
                                )}
                              </p>
                            )}
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={`${FORDSHARE_ACTIVITY_URL_TEMPLATE}${entry.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#0014cc] rounded-lg hover:bg-[#0010a3] transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3 h-3" />
                                Open in FordShare
                              </a>
                            </TooltipTrigger>
                            <TooltipContent
                              className="text-[10px]"
                              style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}
                            >
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      `${FORDSHARE_ACTIVITY_URL_TEMPLATE}${entry.id}`,
                                    );
                                    toast.success('URL copied');
                                  }}
                                  className="p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                <span>
                                  {FORDSHARE_ACTIVITY_URL_TEMPLATE}
                                  {entry.id}
                                </span>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );

                  if (isLast && canEdit) {
                    return (
                      <div key={entry.id} className="flex items-center gap-1.5">
                        {badge}
                        <button
                          onClick={() => setShowFordShareModal(true)}
                          className="w-7 h-7 rounded-lg inline-flex items-center justify-center border-2 border-dashed border-slate-400 text-slate-400 hover:border-slate-600 hover:text-slate-600 transition-all opacity-0 group-hover/fscell:opacity-100"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  }
                  return badge;
                })}
                {entries.length === 0 && canEdit && (
                  <button
                    onClick={() => setShowFordShareModal(true)}
                    className="w-7 h-7 rounded-lg inline-flex items-center justify-center border-2 border-dashed border-slate-400 text-slate-400 hover:border-slate-600 hover:text-slate-600 transition-all opacity-0 group-hover/fscell:opacity-100"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </td>
          );
        }

        // Preview link — chip-based display with popover editor
        if (column.key === 'preview_link') {
          const links: string[] = Array.isArray(campaign.preview_link) ? campaign.preview_link : [];
          const apiFieldName = getApiFieldName(column.key);
          const canEdit = !!(column.editable && editableFields.includes(apiFieldName));
          return (
            <td key={column.id} className={cellClass}>
              <PreviewLinkCell
                links={links}
                canEdit={canEdit}
                onUpdate={(updated) => handleFieldUpdate('preview_link', updated)}
              />
            </td>
          );
        }

        // Check if this field is editable based on user permissions
        const apiFieldName = getApiFieldName(column.key);
        const canUserEditField = column.editable && editableFields.includes(apiFieldName);

        if (canUserEditField) {
          const value = campaign[column.key];
          // For other fields, convert to string
          const stringValue = typeof value === 'string' ? value : (value?.toString() ?? null);

          if (column.type === 'boolean') {
            return (
              <td key={column.id} className={cellClass}>
                <EditableBoolean
                  value={campaign[column.key] as boolean | null}
                  onChange={(value) => handleFieldUpdate(column.key, value)}
                />
              </td>
            );
          } else if (column.type === 'date') {
            const dateValue = stringValue?.split('T')[0] ?? null;
            const formattedDate = formatDate(stringValue);

            return (
              <td key={column.id} className={cellClass}>
                <EditableField
                  value={dateValue}
                  displayValue={formattedDate}
                  onChange={(value) => handleFieldUpdate(column.key, value)}
                  type="date"
                />
              </td>
            );
          } else {
            return (
              <td key={column.id} className={cellClass}>
                <EditableField
                  value={stringValue}
                  onChange={(value) => handleFieldUpdate(column.key, value)}
                  type={column.type}
                />
              </td>
            );
          }
        } else {
          // Non-editable field
          const value = campaign[column.key];
          let displayValue: string;

          if (Array.isArray(value)) {
            // For array fields, join with comma for display
            displayValue = value.length > 0 ? value.join(', ') : '-';
          } else {
            displayValue = value !== null && value !== undefined ? String(value) : '-';
          }

          // Format dates for non-editable date fields
          if (column.type === 'date' && value) {
            const stringValue = typeof value === 'string' ? value : (value?.toString() ?? null);
            displayValue = formatDate(stringValue) || '-';
          }

          return (
            <td key={column.id} className={cellClass}>
              <TruncatedCell title={displayValue !== '-' ? displayValue : undefined}>
                {displayValue}
              </TruncatedCell>
            </td>
          );
        }
      }
    }
  };

  // Get SLA-based background color
  const slaRowClass = getSlaRowClass(campaign.sla_status);

  // Archived rows get special styling
  const archivedClass = campaign.is_archived ? 'opacity-50 bg-gray-100' : '';

  // Highlight class for deep-linked campaigns (from email)
  const highlightClass = isHighlighted ? 'ring-2 ring-blue-500 ring-inset bg-blue-50' : '';

  return (
    <>
      <tr
        id={`campaign-row-${campaign.id}`}
        ref={measureRef}
        data-index={rowIndex}
        className={`transition-all duration-300 hover:shadow-sm ${isUpdating ? 'bg-blue-50' : highlightClass || archivedClass || (showColors ? slaRowClass : (stripeIndex ?? rowIndex) % 2 === 1 ? 'bg-blue-50/60' : '')}`}
      >
        {visibleColumns.map((column) => renderCell(column))}
      </tr>

      {/* Status Progression Modal */}
      {showStatusModal && (
        <StatusProgressionModal
          campaign={campaign}
          isOpen={showStatusModal}
          onClose={handleStatusModalClose}
        />
      )}

      {/* FordShare Modal */}
      <FordShareModal
        isOpen={showFordShareModal}
        onClose={() => {
          setShowFordShareModal(false);
          onHighlight?.(campaign.id);
        }}
        entries={campaign.fordshare_entries ?? []}
        onSave={handleFordShareSave}
        isLoading={mutation.isPending}
      />
    </>
  );
}

export function ContentTrackerPage() {
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [columns, setColumns] = useState<TableColumn[]>(() => loadColumnSettings());
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const defaultFilters = useMemo(
    () => ({
      channel: getDefaultChannelFilter(currentUser?.channel),
      platform: '',
      quarter: '',
      market: getDefaultMarketFilter(currentUser?.market),
      business_unit: '',
      nameplate: '',
      format: '',
      plan_id: '',
    }),
    [currentUser?.market, currentUser?.channel],
  );
  const [filters, setFilters] = useState<typeof defaultFilters>(() => {
    try {
      const saved = sessionStorage.getItem('ct-filters');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return defaultFilters;
  });

  // Show archived campaigns toggle
  const [showArchived, setShowArchived] = useState(() => {
    try {
      const saved = sessionStorage.getItem('ct-show-archived');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return false;
  });

  const [showColors, setShowColors] = useState(() => {
    try {
      const saved = sessionStorage.getItem('ct-show-colors');
      if (saved !== null) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return true;
  });

  // Deep link support: ?campaign=X&highlightDuration=5000&action=status|preview
  // - campaign: human-readable campaign_id to scroll to and highlight
  // - highlightDuration: how long to highlight in ms (default: 3000, use 0 for persistent)
  // - action: 'status' opens StatusProgressionModal, 'preview' navigates to asset preview page
  //   (no default — omitting action just highlights the row)
  const [highlightedCampaignId, setHighlightedCampaignId] = useState<string | null>(null);
  const [statusModalCampaignId, setStatusModalCampaignId] = useState<string | null>(null);
  // Sort state is now driven by TanStack Table's SortingState. We still persist
  // it to sessionStorage and feed it into the existing client-side comparator.
  const [sorting, setSorting] = useState<SortingState>(() => {
    try {
      const saved = sessionStorage.getItem('ct-sort');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed as SortingState;
        // Backward-compat: previous { column, direction } shape
        if (parsed && typeof parsed.column === 'string') {
          return [{ id: parsed.column, desc: parsed.direction === 'desc' }];
        }
      }
    } catch {
      /* ignore */
    }
    return [];
  });
  const hasProcessedDeepLink = useRef(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Parse URL query params on initial load
  const [deepLinkParams] = useState<{
    campaign?: string;
    highlightDuration: number;
    action?: string;
  }>(() => {
    const params = new URLSearchParams(window.location.search);
    const campaign = params.get('campaign') || undefined;
    const highlightDuration = parseInt(params.get('highlightDuration') || '3000', 10);
    const action = params.get('action') || undefined; // No default - only open modal if explicitly requested

    if (campaign) {
      console.log('[DeepLink] Params:', { campaign, highlightDuration, action });
    }
    return { campaign, highlightDuration, action };
  });

  // Keep backwards compatibility
  const campaignFromUrl = deepLinkParams.campaign;

  // Persist filters, sorting, and archived toggle to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('ct-filters', JSON.stringify(filters));
  }, [filters]);
  // Apply the user's assigned channel/market defaults to the active filters.
  //
  // We track the last default we applied (persisted in sessionStorage so it
  // survives a refresh). On first load we fill empty slots; whenever the user's
  // assigned channel/market actually changes, we re-sync the filter to the new
  // default. This prevents a stale sessionStorage value from "sticking" after an
  // admin changes the user's channel (e.g. Paid Search -> Paid Social) while
  // still letting manual in-session filter tweaks persist between renders.
  const lastAppliedDefaults = useRef<{ channel: string; market: string } | null>(null);
  // Initialize once from sessionStorage (useRef has no lazy initializer, so we
  // guard on null instead of reading/parsing storage on every render).
  if (lastAppliedDefaults.current === null) {
    let initial = { channel: '', market: '' };
    try {
      const saved = sessionStorage.getItem('ct-applied-defaults');
      if (saved) initial = JSON.parse(saved);
    } catch {
      /* ignore */
    }
    lastAppliedDefaults.current = initial;
  }
  // Depend on the primitives this effect actually reads (plus whether a user is
  // loaded at all) rather than the `currentUser` object, whose identity changes on
  // every refetch and would re-run the sync needlessly.
  const hasCurrentUser = !!currentUser;
  const currentUserChannel = currentUser?.channel;
  const currentUserMarket = currentUser?.market;
  useEffect(() => {
    if (!hasCurrentUser) return;
    const channelDefault = getDefaultChannelFilter(currentUserChannel);
    const marketDefault = getDefaultMarketFilter(currentUserMarket);
    const prevDefaults = lastAppliedDefaults.current!;

    setFilters((prev) => {
      const next = { ...prev };
      let changed = false;
      // First load (empty slot) OR the assigned default changed -> apply it.
      if (!prev.channel || channelDefault !== prevDefaults.channel) {
        next.channel = channelDefault;
        changed = true;
      }
      if (!prev.market || marketDefault !== prevDefaults.market) {
        next.market = marketDefault;
        changed = true;
      }
      return changed ? next : prev;
    });

    lastAppliedDefaults.current = { channel: channelDefault, market: marketDefault };
    sessionStorage.setItem('ct-applied-defaults', JSON.stringify(lastAppliedDefaults.current));
  }, [hasCurrentUser, currentUserChannel, currentUserMarket]);
  useEffect(() => {
    sessionStorage.setItem('ct-sort', JSON.stringify(sorting));
  }, [sorting]);
  useEffect(() => {
    sessionStorage.setItem('ct-show-archived', JSON.stringify(showArchived));
  }, [showArchived]);
  useEffect(() => {
    sessionStorage.setItem('ct-show-colors', JSON.stringify(showColors));
  }, [showColors]);

  // Track header height so the table fills exactly the remaining viewport.
  const { headerRef, headerHeight } = useHeaderHeight();

  // Load column settings from backend on mount
  useEffect(() => {
    loadColumnSettingsAsync()
      .then((backendColumns) => {
        setColumns(backendColumns);
      })
      .catch((error) => {
        console.warn('Failed to load column settings from backend:', error);
        // Continue with localStorage settings (already loaded in state initialization)
      });
  }, []); // Run once on mount

  const { data, isLoading, isError } = useQuery({
    queryKey: ['campaigns', showArchived],
    queryFn: () => fetchCampaigns(showArchived),
  });

  // Fetch user permissions to determine which fields can be edited
  const { data: permissions } = useQuery({
    queryKey: ['user-permissions'],
    queryFn: getPermissions,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Get editable fields from permissions, default to empty array
  const editableFields = permissions?.editable_campaign_fields ?? [];

  // Get unique values for each filter - memoized to avoid recomputation on filter changes
  // Must be called before early returns to satisfy Rules of Hooks
  const allCampaigns = useMemo(
    () =>
      (data?.campaigns || []).map((c) => ({
        ...c,
        platform: c.platform ? normalizePlatformLabel(c.platform) : c.platform,
      })),
    [data?.campaigns],
  );
  // Type-guard filter to properly narrow string | null | undefined to string
  const isNonEmptyString = (v: string | null | undefined): v is string => Boolean(v);

  // Filter keys for type safety
  type FilterKey = keyof typeof filters;
  const filterKeys: FilterKey[] = [
    'channel',
    'platform',
    'quarter',
    'market',
    'business_unit',
    'nameplate',
    'format',
    'plan_id',
  ];

  // Multi-select filter matching: checks if a comma-separated filter value includes the field value
  const matchesFilter = (filterVal: string, fieldVal: string | null | undefined): boolean =>
    !filterVal || (fieldVal != null && filterVal.split(',').includes(fieldVal));

  // Quarter matching: a campaign's quarter can be comma-joined (e.g. "Q1, Q2")
  // when it spans multiple quarters. Match if ANY of the campaign's quarters is
  // selected, so picking Q1 + Q2 returns every line containing Q1 or Q2 — not
  // only rows that are exclusively "Q1" / "Q2".
  const matchesQuarterFilter = (
    filterVal: string,
    fieldVal: string | null | undefined,
  ): boolean => {
    if (!filterVal) return true;
    if (fieldVal == null) return false;
    const selected = filterVal.split(',');
    return fieldVal.split(',').some((q) => selected.includes(q.trim()));
  };

  // Dispatch to the quarter-aware matcher for the quarter column; every other
  // column keeps the exact-match behaviour.
  const matchesFilterForKey = (
    key: FilterKey,
    filterVal: string,
    fieldVal: string | null | undefined,
  ): boolean =>
    key === 'quarter'
      ? matchesQuarterFilter(filterVal, fieldVal)
      : matchesFilter(filterVal, fieldVal);

  // Multi-directional filtering: compute available options for each filter
  // based on all OTHER active filters (not the one we're computing options for)
  const availableOptions = useMemo(() => {
    const computeOptions = (filterKey: FilterKey): string[] => {
      // Apply all filters EXCEPT the one we're computing options for
      const filtered = allCampaigns.filter((campaign) => {
        for (const key of filterKeys) {
          if (key === filterKey || !filters[key]) continue;
          if (!matchesFilterForKey(key, filters[key], campaign[key])) return false;
        }
        return true;
      });
      // Quarter is always the four calendar quarters (campaign values can be
      // comma-joined like "Q1, Q2"), narrowed to those actually present given
      // the other active filters.
      if (filterKey === 'quarter') {
        const present = new Set(
          filtered.flatMap((c) =>
            (c.quarter ?? '')
              .split(',')
              .map((q) => q.trim())
              .filter(Boolean),
          ),
        );
        return ['Q1', 'Q2', 'Q3', 'Q4'].filter((q) => present.has(q));
      }
      return [...new Set(filtered.map((c) => c[filterKey]).filter(isNonEmptyString))].sort();
    };

    return {
      channel: computeOptions('channel'),
      platform: computeOptions('platform'),
      quarter: computeOptions('quarter'),
      market: computeOptions('market'),
      business_unit: computeOptions('business_unit'),
      nameplate: computeOptions('nameplate'),
      format: computeOptions('format'),
      plan_id: computeOptions('plan_id'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCampaigns, filters]);

  // Auto-clear invalid filter selections when they become unavailable
  // (e.g., selecting Market=DE might make a previously selected Platform invalid)
  // Handles comma-separated multi-select values by removing individual unavailable values
  useEffect(() => {
    if (isLoading) return;

    const newFilters = { ...filters };
    let changed = false;

    for (const key of filterKeys) {
      const value = filters[key];
      if (!value) continue;
      const parts = value.split(',');
      const validParts = parts.filter((v) => availableOptions[key].includes(v));
      const newValue = validParts.join(',');
      if (newValue !== value) {
        newFilters[key] = newValue;
        changed = true;
      }
    }

    if (changed) {
      setFilters(newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableOptions, isLoading]);

  // Handle deep link: ?campaign=X&highlightDuration=5000&action=status
  useEffect(() => {
    if (campaignFromUrl && allCampaigns.length > 0 && !hasProcessedDeepLink.current) {
      // Find campaign by campaign_id (with fallback for old 4-part IDs from pre-migration links)
      const targetCampaign = findCampaignByCampaignId(allCampaigns, campaignFromUrl);

      if (targetCampaign) {
        hasProcessedDeepLink.current = true;

        // Clear filters so the target campaign is guaranteed to be visible
        setFilters(defaultFilters);
        setHighlightedCampaignId(targetCampaign.id);

        // Clear the URL param (keeps the page clean)
        window.history.replaceState({}, '', window.location.pathname);

        // Scroll to the row after a short delay (wait for render)
        setTimeout(() => {
          scrollToCampaign(targetCampaign.id);

          // Handle action param - which modal to open (if any)
          const { action, highlightDuration } = deepLinkParams;

          if (action === 'status') {
            // Open status modal after brief delay so user sees the highlighted row
            setTimeout(() => {
              setStatusModalCampaignId(targetCampaign.id);
            }, 500);
          } else if (action === 'preview') {
            // Navigate to the asset preview page
            navigate({
              to: '/campaigns/$campaignId/preview',
              params: { campaignId: targetCampaign.id },
            });
          } else {
            // No action or action=none: just highlight, clear after duration
            if (highlightDuration > 0) {
              setTimeout(() => {
                setHighlightedCampaignId(null);
              }, highlightDuration);
            }
            // If highlightDuration is 0, highlight stays until user interacts
          }
          // Future actions can be added here: 'edit', 'assets', etc.
        }, 100);
      }
    }
    // rowVirtualizer is a stable instance; only referenced inside deferred callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignFromUrl, allCampaigns, deepLinkParams, navigate, defaultFilters]);

  // Highlight the row the user was last viewing in the asset preview page
  useEffect(() => {
    if (allCampaigns.length === 0 || campaignFromUrl) return; // don't interfere with deep links
    const lastViewed = sessionStorage.getItem('ct-last-viewed-campaign');
    if (!lastViewed) return;
    sessionStorage.removeItem('ct-last-viewed-campaign');

    setHighlightedCampaignId(lastViewed);
    setTimeout(() => {
      scrollToCampaign(lastViewed);
    }, 100);
    setTimeout(() => setHighlightedCampaignId(null), 3000);
    // rowVirtualizer is a stable instance; only referenced inside deferred callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCampaigns, campaignFromUrl]);

  // Clear the highlighted campaign after a short delay to smooth out the UX when closing overlays
  const clearHighlightAfterDelay = () => {
    if (highlightedCampaignId) {
      setTimeout(() => {
        setHighlightedCampaignId(null);
      }, 500);
    }
  };

  // Handle status modal close from deep link
  const handleStatusModalClose = () => {
    setStatusModalCampaignId(null);
    clearHighlightAfterDelay();
  };

  // Apply filters
  const filteredCampaigns = allCampaigns.filter((campaign) => {
    if (!matchesFilter(filters.channel, campaign.channel)) return false;
    if (!matchesFilter(filters.platform, campaign.platform)) return false;
    if (!matchesQuarterFilter(filters.quarter, campaign.quarter)) return false;
    if (!matchesFilter(filters.market, campaign.market)) return false;
    if (!matchesFilter(filters.business_unit, campaign.business_unit)) return false;
    if (!matchesFilter(filters.nameplate, campaign.nameplate)) return false;
    if (!matchesFilter(filters.format, campaign.format)) return false;
    if (!matchesFilter(filters.plan_id, campaign.plan_id)) return false;
    return true;
  });

  // Quarter sort order
  const QUARTER_ORDER = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Rank a (possibly comma-joined) quarter value by its EARLIEST quarter, so
  // "Q1, Q2" sorts alongside "Q1" rather than dropping to the bottom. Unknown/
  // empty values rank last.
  const quarterRank = (value: string | null | undefined): number => {
    const ranks = (value ?? '')
      .split(',')
      .map((q) => QUARTER_ORDER.indexOf(q.trim()))
      .filter((i) => i !== -1);
    return ranks.length ? Math.min(...ranks) : QUARTER_ORDER.length;
  };

  // Apply sorting — TanStack drives the header UI/toggle (manualSorting), but we
  // keep the existing battle-tested comparator with all its special cases.
  const activeSort = sorting[0];
  const campaigns = activeSort
    ? [...filteredCampaigns].sort((a, b) => {
        const column = activeSort.id;
        const direction: 'asc' | 'desc' = activeSort.desc ? 'desc' : 'asc';
        let comparison: number;

        // Map column key to actual campaign property (handle special cases)
        const sortKey = column === 'last_edited' ? 'last_edited_at' : column;

        // Get values based on column
        const aVal = a[sortKey as keyof Campaign];
        const bVal = b[sortKey as keyof Campaign];

        // Handle special sort orders
        if (column === 'status') {
          const aIndex = STATUS_ORDER.indexOf(a.status || '');
          const bIndex = STATUS_ORDER.indexOf(b.status || '');
          const aOrder = aIndex === -1 ? STATUS_ORDER.length : aIndex;
          const bOrder = bIndex === -1 ? STATUS_ORDER.length : bIndex;
          comparison = aOrder - bOrder;
        } else if (column === 'quarter') {
          comparison = quarterRank(aVal as string | null) - quarterRank(bVal as string | null);
        } else if (
          column === 'start_date' ||
          column === 'end_date' ||
          column === 'live_date' ||
          column === 'last_edited'
        ) {
          // Date sorting
          const aDate = aVal ? new Date(String(aVal)).getTime() : 0;
          const bDate = bVal ? new Date(String(bVal)).getTime() : 0;
          comparison = aDate - bDate;
        } else if (column === 'fordshare_entries') {
          // Sort by first entry's ID numerically
          const aEntries = aVal as FordShareEntry[] | null;
          const bEntries = bVal as FordShareEntry[] | null;
          const aNum = Number(aEntries?.[0]?.id) || 0;
          const bNum = Number(bEntries?.[0]?.id) || 0;
          comparison = aNum - bNum;
        } else if (column === 'live') {
          // Boolean sorting (true first in asc)
          const aBool = aVal === true ? 1 : 0;
          const bBool = bVal === true ? 1 : 0;
          comparison = aBool - bBool;
        } else {
          // Alphabetical sorting for strings
          const aStr = String(aVal || '').toLowerCase();
          const bStr = String(bVal || '').toLowerCase();
          comparison = aStr.localeCompare(bStr);
        }

        return direction === 'asc' ? comparison : -comparison;
      })
    : filteredCampaigns;

  // Collapsed plan groups (session-scoped, like the other view state). Keys are
  // plan_id values, with NO_PLAN_KEY standing in for campaigns without a plan.
  const [collapsedPlans, setCollapsedPlans] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem('ct-collapsed-plans');
      if (saved) return new Set(JSON.parse(saved));
    } catch {
      /* ignore */
    }
    return new Set();
  });
  const togglePlanCollapsed = (key: string) => {
    setCollapsedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        sessionStorage.setItem('ct-collapsed-plans', JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Group the sorted campaigns by plan_id and flatten into a single row model
  // (group header rows interleaved with campaign rows) so the existing
  // virtualizer/table keep working over one flat array. Groups are ordered by
  // plan_id (numeric-aware); campaigns without a plan sink to the bottom under
  // "No Plan ID". Within a group, rows keep the active sort order.
  const groupedByPlan = new Map<string | null, Campaign[]>();
  for (const c of campaigns) {
    const key = c.plan_id ?? null;
    const group = groupedByPlan.get(key);
    if (group) group.push(c);
    else groupedByPlan.set(key, [c]);
  }
  // Groups are ordered by plan approval date, newest first: recently approved
  // plans are the ones being actively worked, so they surface at the top.
  // Plans without an approval timestamp sort after dated ones; the "No Plan
  // ID" group always sinks to the bottom. Plan ID (numeric-aware) tie-breaks.
  const groupApprovedAt = (planId: string): number => {
    const ts = groupedByPlan.get(planId)?.[0]?.plan_approved_at;
    return ts ? new Date(ts).getTime() : 0;
  };
  const orderedPlanIds = [...groupedByPlan.keys()].sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    const dateDiff = groupApprovedAt(b) - groupApprovedAt(a);
    if (dateDiff !== 0) return dateDiff;
    return a.localeCompare(b, undefined, { numeric: true });
  });
  const tableRows: CtTableRow[] = [];
  {
    let ordinal = 0;
    for (const planId of orderedPlanIds) {
      const group = groupedByPlan.get(planId)!;
      const collapsed = collapsedPlans.has(planId ?? NO_PLAN_KEY);
      tableRows.push({
        kind: 'group',
        planId,
        count: group.length,
        hasDco: group.some((c) => isDcoEnabled(c.dco_status)),
        collapsed,
        approvedAt: group[0]?.plan_approved_at ?? null,
      });
      if (collapsed) continue;
      for (const campaign of group) {
        tableRows.push({ kind: 'campaign', campaign, ordinal: ordinal++ });
      }
    }
  }

  // Keep refs to the latest row model / campaign list so deferred scroll
  // callbacks (deep link / last-viewed run inside setTimeout) resolve against
  // the current render rather than a stale closure snapshot.
  const tableRowsRef = useRef(tableRows);
  tableRowsRef.current = tableRows;
  const campaignsRef = useRef(campaigns);
  campaignsRef.current = campaigns;

  const clearFilters = () => {
    setFilters({
      channel: '',
      platform: '',
      quarter: '',
      market: '',
      business_unit: '',
      nameplate: '',
      format: '',
      plan_id: '',
    });
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  const handleColumnSettingsSave = (newColumns: ColumnConfig[]) => {
    // Only update columns that already exist, to ensure type safety.
    // Honor an incoming width when provided (e.g. "Reset to Default" restores
    // the default per-column widths), otherwise keep the existing width.
    const updatedColumns = newColumns
      .map((newCol): TableColumn | null => {
        const existingCol = columns.find((c) => c.id === newCol.id);
        if (!existingCol) return null;
        return {
          ...existingCol,
          visible: newCol.visible,
          ...(newCol.width !== undefined ? { width: newCol.width } : {}),
        };
      })
      .filter((col): col is TableColumn => col !== null);

    setColumns(updatedColumns);
    saveColumnSettings(updatedColumns);
  };

  const visibleColumns = columns.filter((col) => col.visible);

  // Lookup from TanStack column id (== TableColumn.key) back to our TableColumn,
  // so the header renderer can reuse editable/centered/sticky logic.
  const columnByKey = useMemo(() => {
    const map = new Map<string, TableColumn>();
    for (const col of columns) map.set(col.key, col);
    return map;
  }, [columns]);

  // Column sizing state derived from saved widths, keyed by TanStack column id.
  const columnSizing = useMemo<ColumnSizingState>(() => {
    const sizing: ColumnSizingState = {};
    for (const col of visibleColumns) {
      if (typeof col.width === 'number') sizing[col.key] = col.width;
    }
    return sizing;
  }, [visibleColumns]);

  // Transient sizing applied live while a column is being dragged. Keeping the
  // in-progress widths in local state (instead of `columns`) gives smooth visual
  // feedback without re-rendering the whole table or writing to storage on every
  // pointer move. It is committed to `columns`/storage once the drag finishes.
  const [liveColumnSizing, setLiveColumnSizing] = useState<ColumnSizingState | null>(null);
  const effectiveColumnSizing = liveColumnSizing ?? columnSizing;

  // Update only the transient sizing during a resize; persistence happens on
  // resize end (see the effect below) to avoid jank and localStorage churn.
  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = (updater) => {
    setLiveColumnSizing((prev) => {
      const base = prev ?? columnSizing;
      return typeof updater === 'function' ? updater(base) : updater;
    });
  };

  // Headless TanStack column model: we use it only for the column model (sizing +
  // sorting state + header objects). Cell bodies are still rendered by CampaignRow.
  const columnDefs = useMemo<ColumnDef<Campaign>[]>(
    () =>
      visibleColumns.map((col) => ({
        id: col.key,
        // TanStack's getCanSort() only returns true when a column has an
        // accessor. Without one, header clicks never toggle sorting. The
        // returned value is intentionally unused: manualSorting is enabled so
        // our client-side comparator does the ordering, and cells are rendered
        // by CampaignRow (never via getValue). Returning a constant keeps the
        // column sortable while avoiding an unsafe keyof-Campaign cast for the
        // non-data keys ('actions', 'last_edited').
        accessorFn: () => null,
        header: col.label,
        size: col.width ?? DEFAULT_COLUMN_WIDTH,
        minSize: MIN_COLUMN_WIDTH,
        maxSize: MAX_COLUMN_WIDTH,
        enableSorting: col.id !== 'actions',
      })),
    [visibleColumns],
  );

  const table = useReactTable({
    data: campaigns,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    state: { sorting, columnSizing: effectiveColumnSizing },
    onSortingChange: setSorting,
    onColumnSizingChange: handleColumnSizingChange,
    manualSorting: true, // sorting handled by the existing client-side comparator
    // The comparator only honors sorting[0]. Disable multi-sort so Shift+click
    // can't push extra entries that getIsSorted() would highlight while the data
    // ignores them (UI/data desync).
    enableMultiSort: false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
  });

  // Commit the transient resize widths to `columns`/storage once the drag ends.
  // `columnSizingInfo.isResizingColumn` is the active column id while dragging
  // and `false` when the pointer is released; we persist on that transition so
  // localStorage/backend are written once per resize rather than per pointer move.
  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;
  useEffect(() => {
    if (isResizingColumn || liveColumnSizing === null) return;
    const next = liveColumnSizing;
    setLiveColumnSizing(null);
    const updated = columns.map((col) =>
      next[col.key] !== undefined ? { ...col, width: Math.round(next[col.key]) } : col,
    );
    setColumns(updated);
    saveColumnSettings(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizingColumn]);

  const isFiltered = activeFilterCount > 0;

  // Row virtualizer: render only the rows currently on screen. Operates over
  // the flattened row model (group headers + campaign rows).
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: (index) =>
      tableRowsRef.current[index]?.kind === 'group' ? GROUP_ROW_HEIGHT : ROW_HEIGHT,
    overscan: 12,
    getItemKey: (index) => {
      const row = tableRows[index];
      return row.kind === 'group' ? `plan:${row.planId ?? NO_PLAN_KEY}` : row.campaign.id;
    },
  });

  // Scroll a campaign (by id) into view. With virtualization an off-screen row
  // isn't mounted until it enters the window, so a deep-linked/last-viewed row
  // only shows its highlight ring once scrolled to. Rows are dynamically measured
  // (see measureRef), but the first scroll uses estimated offsets, so re-issue it
  // on the next two frames once real heights are known to land on the target.
  const scrollToCampaign = useCallback(
    (id: string) => {
      const doScroll = () => {
        const index = tableRowsRef.current.findIndex(
          (row) => row.kind === 'campaign' && row.campaign.id === id,
        );
        if (index !== -1) {
          rowVirtualizer.scrollToIndex(index, { align: 'center' });
          return;
        }
        // Target row is inside a collapsed plan group — expand it; the rAF
        // retries below re-run doScroll once the rows are back in the model.
        const target = campaignsRef.current.find((c) => c.id === id);
        if (!target) return;
        const key = target.plan_id ?? NO_PLAN_KEY;
        setCollapsedPlans((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          try {
            sessionStorage.setItem('ct-collapsed-plans', JSON.stringify([...next]));
          } catch {
            /* ignore */
          }
          return next;
        });
      };
      doScroll();
      requestAnimationFrame(() => {
        doScroll();
        requestAnimationFrame(doScroll);
      });
    },
    // rowVirtualizer is a stable instance; campaignsRef is a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (isError) {
    return (
      <>
        <Breadcrumb />
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            Failed to load campaigns. Please try again later.
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div ref={headerRef} className="shrink-0">
        <ContentTrackerHeader
          totalCampaigns={allCampaigns.length}
          filteredCount={campaigns.length}
          isFiltered={isFiltered}
          filters={filters}
          onFilterChange={setFilters}
          availableOptions={availableOptions}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          showColors={showColors}
          onShowColorsChange={setShowColors}
          activeFilterCount={activeFilterCount}
          onClearFilters={clearFilters}
          onTeamChange={() => setColumns(loadColumnSettings())}
          onOpenColumnSettings={() => setShowColumnSettings(true)}
        />
      </div>
      <div className={PAGE_PADDING}>
        {/* Campaigns Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {isLoading ? (
            <ContentTrackerTableSkeleton />
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">
                {isFiltered ? 'No campaigns match the selected filters' : 'No campaigns found'}
              </p>
            </div>
          ) : (
            <div
              ref={tableContainerRef}
              className="overflow-auto"
              style={{
                maxHeight: tableScrollMaxHeight(headerHeight),
                scrollbarWidth: 'thin',
                scrollbarColor: '#D1D5DB #F3F4F6',
              }}
            >
              <table
                className="min-w-full"
                style={{ tableLayout: 'fixed', width: table.getTotalSize() }}
              >
                <colgroup>
                  {table.getVisibleLeafColumns().map((leaf) => (
                    <col key={leaf.id} style={{ width: leaf.getSize() }} />
                  ))}
                </colgroup>
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-[1]">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const column = columnByKey.get(header.column.id);
                        if (!column) return null;
                        const isCentered = column.id === 'actions' || column.id === 'status';
                        const isSortable = header.column.getCanSort();
                        const sortDir = header.column.getIsSorted(); // false | 'asc' | 'desc'
                        const isCurrentlySorted = sortDir !== false;

                        // Check if this column is editable by the current user
                        const apiFieldName = getApiFieldName(column.key);
                        const isEditableByUser =
                          column.editable && editableFields.includes(apiFieldName);

                        return (
                          <th
                            key={header.id}
                            onClick={
                              isSortable ? header.column.getToggleSortingHandler() : undefined
                            }
                            className={`relative px-4 py-3 ${isCentered ? 'text-center' : 'text-left'} text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-all select-none ${
                              isEditableByUser ? 'text-blue-600' : 'text-slate-600'
                            } ${column.id === 'last_edited' ? 'sticky right-0 bg-slate-50 shadow-[-2px_0_4px_rgba(0,0,0,0.05)]' : ''} ${
                              isSortable ? 'cursor-pointer hover:bg-slate-100' : ''
                            } ${isCurrentlySorted ? 'bg-blue-50' : ''}`}
                            title={isSortable ? `Click to sort by ${column.label}` : undefined}
                          >
                            <div
                              className={`flex items-center gap-2 ${isCentered ? 'justify-center' : ''}`}
                            >
                              <span className="truncate">{column.label}</span>
                              {isSortable &&
                                (isCurrentlySorted ? (
                                  <svg
                                    className="w-4 h-4 text-[#0014cc] flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-4 h-4 text-slate-300 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                    />
                                  </svg>
                                ))}
                            </div>
                            {/* Resize handle — absolutely positioned on the right edge */}
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={(e) => e.stopPropagation()}
                                role="separator"
                                aria-orientation="vertical"
                                className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none touch-none hover:bg-blue-400/60 ${
                                  header.column.getIsResizing() ? 'bg-blue-500' : ''
                                }`}
                              />
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {(() => {
                    const virtualRows = rowVirtualizer.getVirtualItems();
                    const totalSize = rowVirtualizer.getTotalSize();
                    const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
                    const paddingBottom = virtualRows.length
                      ? totalSize - virtualRows[virtualRows.length - 1].end
                      : 0;
                    return (
                      <>
                        {paddingTop > 0 && (
                          <tr>
                            <td colSpan={visibleColumns.length} style={{ height: paddingTop }} />
                          </tr>
                        )}
                        {virtualRows.map((vr) => {
                          const row = tableRows[vr.index];
                          if (row.kind === 'group') {
                            const collapseKey = row.planId ?? NO_PLAN_KEY;
                            return (
                              <tr
                                key={`plan:${collapseKey}`}
                                ref={rowVirtualizer.measureElement}
                                data-index={vr.index}
                                className="bg-slate-100/90 cursor-pointer hover:bg-slate-200/80 transition-colors"
                                onClick={() => togglePlanCollapsed(collapseKey)}
                              >
                                <td colSpan={visibleColumns.length} className="px-4 py-2">
                                  {/* Sticky so the label stays visible while the
                                      wide table is scrolled horizontally */}
                                  <div
                                    className="sticky left-4 flex items-center gap-2 w-max"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={!row.collapsed}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        togglePlanCollapsed(collapseKey);
                                      }
                                    }}
                                  >
                                    {row.collapsed ? (
                                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                                    ) : (
                                      <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                                    )}
                                    <Hash className="w-3.5 h-3.5 text-slate-400" />
                                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                                      {row.planId ? `Plan ${row.planId}` : 'No Plan ID'}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      {row.count} campaign{row.count !== 1 ? 's' : ''}
                                    </span>
                                    {row.approvedAt && (
                                      <span className="text-xs text-slate-400">
                                        · approved{' '}
                                        {new Date(row.approvedAt).toLocaleDateString('en-GB', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric',
                                        })}
                                      </span>
                                    )}
                                    {row.hasDco && (
                                      <DcoBadge
                                        compact
                                        tooltip="This plan includes DCO (Dynamic Creative Optimization) campaign lines."
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          const campaign = row.campaign;
                          return (
                            <CampaignRow
                              key={campaign.id}
                              campaign={campaign}
                              rowIndex={vr.index}
                              stripeIndex={row.ordinal}
                              measureRef={rowVirtualizer.measureElement}
                              visibleColumns={visibleColumns}
                              editableFields={editableFields}
                              isHighlighted={highlightedCampaignId === campaign.id}
                              showColors={showColors}
                              openStatusModal={statusModalCampaignId === campaign.id}
                              onStatusModalClose={handleStatusModalClose}
                              onHighlight={(id) => {
                                setHighlightedCampaignId(id);
                                setTimeout(() => {
                                  scrollToCampaign(id);
                                }, 300);
                                setTimeout(() => setHighlightedCampaignId(null), 3000);
                              }}
                            />
                          );
                        })}
                        {paddingBottom > 0 && (
                          <tr>
                            <td colSpan={visibleColumns.length} style={{ height: paddingBottom }} />
                          </tr>
                        )}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Column Settings Modal */}
        {showColumnSettings && (
          <ColumnSettings
            columns={columns}
            onSave={handleColumnSettingsSave}
            onClose={() => setShowColumnSettings(false)}
          />
        )}
      </div>
      <div className="mt-auto shrink-0">
        <MapSyncFooter />
      </div>
    </div>
  );
}
